import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import '@blazemeter/blaze-ct-ui-components/dist/blaze-ct-ui-components.css';
import '@blazemeter/blaze-ct-ui-components/blaze-bootstrap/dist/css/blaze-bootstrap.css';
import 'assets/styles/blazeSvStyles.css';
import MockServices from 'views/MockServices/MockServiceTabs';
import LearnMore from 'views/LearnMore/LearnMore';
import CommonHeader from 'views/Layout/CommonHeader/CommonHeader';
import TopMenu from 'views/Layout/TopMenu/TopMenu';
import NotFound from 'views/NotFound/NotFound';
import { ApplicationState } from 'store/rootReducer';
import Toasts from 'components/toasts/Toasts';
import DevOptionsPanel from 'components/devOptions/DevOptionsPanel';
import { hideToast } from 'store/ui/toasts/toastsActions';
import { connect } from 'react-redux';
import { InAppHelp } from 'helpers/in-app-help';
import FullPageLoader from 'components/loaders/FullPageLoader';
import { WithTranslation, withTranslation } from 'react-i18next';
import {
  clearViewAsUser,
  fetchBlazeUserConfigThunk,
  setViewAsUser,
  setVisitedMockServicesThunk,
} from 'store/blazeUserConfig/blazeUserConfigActions';

import YellowWarningBar from 'components/alerts/YellowWarningBar';
import AssetCatalog from 'views/AssetCatalog/AssetCatalog';
import Analytics from 'views/Analytics/Analytics';
import { VIEW_AS_QUERY_PARAM } from 'constants/bzm-constants';
import { ErrorBoundary } from 'components/errorBoundary/ErrorBoundary';
import MixpanelService from './utils/mixpanelService';
import { FIRST_TIME_VISIT_USER } from './utils/mixpanelEvents';
import { LEARN_MORE } from './constants/redux-store-contexts';
import BzmBackdrop from 'components/backdrop/BzmBackdrop';
import i18n from './i18n';
import { getBlazemeterBillingPageUrl, getMockServiceUsageStatus } from './utils/bzmUserConfigUtil';
import _ from 'lodash';
import Configurations from './views/Configurations/Configurations';
import Admin from 'views/Admin/Admin';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';

const mapStateToProps = (state: ApplicationState) => ({
  blazeUserConfig: state.blazeUserConfig,
  isPrivateCloudReadOnly: state.blazeUserConfig.isPrivateCloudReadOnly,
});

const mapDispatchToProps = {
  hideToast,
  setViewAsUser,
  clearViewAsUser,
  fetchBlazeUserConfig: fetchBlazeUserConfigThunk,
  setVisitedMockServices: setVisitedMockServicesThunk,
};

type DispatchProps = typeof mapDispatchToProps;
type AppStateProps = ReturnType<typeof mapStateToProps>;

export class App extends React.Component<DispatchProps & AppStateProps & WithTranslation & RouteComponentProps> {
  componentDidMount() {
    this.checkViewAs();
    try {
      InAppHelp();
    } catch (e) {
      console.error('Cannot load elev.io inAppHelp');
    }
  }

  componentDidUpdate(prevProps: Readonly<DispatchProps & AppStateProps & WithTranslation & RouteComponentProps>): void {
    if (
      this.props.blazeUserConfig.blazeUserConfig != null &&
      this.props.blazeUserConfig.blazeUserConfig !== prevProps.blazeUserConfig.blazeUserConfig
    ) {
      if (!this.props.blazeUserConfig.blazeUserConfig.user.visitedMockServices) {
        MixpanelService.track(FIRST_TIME_VISIT_USER(LEARN_MORE), true);
        this.redirectToLearnMore();
      }
    }
  }

  checkViewAs = () => {
    const viewAs = this.getViewAsParam();
    if (!this.props.blazeUserConfig.viewAsUser && viewAs) {
      this.props.setViewAsUser(viewAs);
      this.props.fetchBlazeUserConfig();
    }
  };

  getViewAsParam = () => {
    const queryParams = new URLSearchParams(this.props.location.search);
    const viewAsUser = queryParams.get(VIEW_AS_QUERY_PARAM);
    if (viewAsUser && parseInt(viewAsUser)) {
      return parseInt(viewAsUser);
    }
    return null;
  };

  viewAsYourself = () => {
    this.props.clearViewAsUser();
    // remove the view_as query param from the url
    const queryParams = new URLSearchParams(this.props.location.search);
    queryParams.delete(VIEW_AS_QUERY_PARAM);
    // force reload of the page
    this.props.history.push(this.props.location.pathname + queryParams.toString() ? '?' + queryParams.toString() : '');
    window.location.reload();
  };

  redirectToLearnMore = () => {
    this.props.setVisitedMockServices();

    //Wait for 5 seconds before we redirect to Learn More page-testing purpose in debug mode
    // otherwise before clicking continue button in debug mode app in TGM app was in Learn more page.
    setTimeout(() => {
      MixpanelService.track(FIRST_TIME_VISIT_USER(LEARN_MORE), true);
      console.log('Waiting before redirect');
    }, 5000);

    this.props.history.push('/learn-more');
  };

  getYellowBar = () => {
    const usageStatus = getMockServiceUsageStatus();
    if (this.props.isPrivateCloudReadOnly === false && (_.isNil(usageStatus) || usageStatus === 'OK')) {
      return;
    }

    const messages = [];
    const noMargin = 'p-0 m-0';
    if (this.props.isPrivateCloudReadOnly) {
      messages.push(<p className={noMargin}>{i18n.t('common:warningMsgs.privateCloudReadOnly')}</p>);
    }

    if (usageStatus !== 'OK') {
      switch (usageStatus) {
        case 'OVER':
          messages.push(
            <p className={noMargin}>
              {i18n.t('common:warningMsgs.limitReached')}{' '}
              <a target={'_blank'} href={'https://guide.blazemeter.com/hc/en-us/requests/new'} rel="noreferrer">
                Contact us
              </a>{' '}
              or <a href={getBlazemeterBillingPageUrl(true) + '?openUpgrade'}>upgrade your plan</a>.
            </p>,
          );
          break;
        case 'REACHING':
          messages.push(
            <p
              className={noMargin}
              dangerouslySetInnerHTML={{ __html: i18n.t('common:warningMsgs.limitReaching') }}
            ></p>,
          );
          break;
      }
    }
    return <YellowWarningBar text={messages} />;
  };

  render() {
    let mainContent: any;
    //Show the app contents only when config is loaded
    if (this.props.blazeUserConfig.blazeUserConfig != null) {
      mainContent = (
        <>
          <TopMenu
            blazeUserConfig={this.props.blazeUserConfig.blazeUserConfig}
            isViewAs={this.props.blazeUserConfig.viewAsUser}
            viewAsYourself={this.viewAsYourself}
          />

          <ErrorBoundary>
            {this.getYellowBar()}
            <Switch>
              <Redirect from="/" exact to="/mock-services" />
              <Redirect from="/asset-catalog" exact to="/asset-catalog/transactions" />
              <Route path="/mock-services" component={MockServices} />
              <Route
                path={[
                  '/asset-catalog',
                  '/asset-catalog/transactions',
                  '/asset-catalog/mar-files',
                  '/asset-catalog/blueprints',
                ]}
                component={AssetCatalog}
              />
              <Route path="/analytics" exact component={Analytics} />
              <Route path="/analytics/:slug" component={Analytics} />
              <Route path="/learn-more" exact component={LearnMore} />
              <Route path="/configurations" exact component={Configurations} />
              <Route
                path={[
                  '/admin',
                  '/admin/usage-report',
                  '/admin/roi-insights',
                  '/admin/auto-shutdown-settings',
                  '/admin/memory-settings',
                  '/admin/audit-log',
                ]}
                component={Admin}
              />
              <Route component={NotFound} />
            </Switch>
          </ErrorBoundary>
        </>
      );
    } else {
      mainContent = (
        <div style={{ height: '100vh' }}>
          <FullPageLoader />
        </div>
      );
    }

    return (
      //Single Drag and Drop provider
      <DndProvider backend={HTML5Backend}>
        <BzmBackdrop />
        <div className="main">
          <CommonHeader />
          <Toasts />
          {mainContent}
        </div>
        {process.env.NODE_ENV === 'development' && <DevOptionsPanel />}
      </DndProvider>
    );
  }
}

export default withRouter(withTranslation()(connect(mapStateToProps, mapDispatchToProps)(App)));
